import { Sound } from "@babylonjs/core"
import { CCUtils } from "../../../../common/CCUtils"
import { log } from "../../../../common/Logging"
import { MazeStore } from "../MazeStore"

export type SoundLoader = (resolveOnDone?: boolean) => Promise<Sound>

export class MazeAudio {
    private static innerInstance: MazeAudio

    static get instance() {
        return this.innerInstance || (this.innerInstance = new this())
    }

    static newInstance() {
        return (this.innerInstance = new this())
    }

    static async asyncPlaySound(sound: Sound) {
        return new Promise<void>((resolve) => {
            try {
                sound.play()
                sound.onEndedObservable.addOnce(() => resolve())
            } catch (error) {
                log.debug(error)
                resolve()
            }
        })
    }

    loadMazeAudioWithPrefix =
        (name: string): SoundLoader =>
        (resolveOnDone = true) => {
            return new Promise((resolve) => {
                const sound = new Sound(
                    name,
                    CCUtils.asset(`maze/audio/addiction-maze-${name}`),
                    MazeStore.instance.scene,
                    () => {
                        if (resolveOnDone) {
                            sound.play()
                            sound.onEndedObservable.addOnce(() =>
                                resolve(sound),
                            )
                        } else {
                            resolve(sound)
                        }
                    },
                )
            })
        }

    loadMazeAudio = (name: string) =>
        new Sound(
            name,
            CCUtils.asset(`maze/audio/${name}`),
            MazeStore.instance.scene,
        )

    // These have to be after the others or it causes a typescript error.
    /* tslint:disable:member-ordering */
    intro = this.loadMazeAudioWithPrefix("n1.mp3")
    example1 = this.loadMazeAudioWithPrefix("n2.mp3")
    teenFinishedInOneMinuteRight = this.loadMazeAudioWithPrefix("n4.mp3")
    teenFinishedInOneMinuteWrong = this.loadMazeAudioWithPrefix("n5.mp3")
    tryExampleAgain = this.loadMazeAudioWithPrefix("n6.mp3")
    example2 = this.loadMazeAudioWithPrefix("n7.mp3")
    teenFinishedInOneMinuteRight2 = this.loadMazeAudioWithPrefix("n9.mp3")
    teenFinishedInOneMinuteWrong2 = this.loadMazeAudioWithPrefix("n10.mp3")

    yourTurn = this.loadMazeAudioWithPrefix("n11.mp3")
    youGetTwoMinutes = this.loadMazeAudioWithPrefix("n12.mp3")
    startMaze = this.loadMazeAudioWithPrefix("n13.mp3")

    wrongCouldntGetOut = this.loadMazeAudioWithPrefix("n14.mp3")
    rightCouldntGetOut = this.loadMazeAudioWithPrefix("n15.mp3")
    wasItHarderThanThought = this.loadMazeAudioWithPrefix("n16.mp3")
    mazeLookedEasy = this.loadMazeAudioWithPrefix("n17.mp3")
    mazeWasHard = this.loadMazeAudioWithPrefix("n18.mp3")

    nonUser1 = this.loadMazeAudioWithPrefix("game1-n1.mp3")
    nonUser2 = this.loadMazeAudioWithPrefix("game1-n2.mp3")
    nonUser3 = this.loadMazeAudioWithPrefix("game1-n3.mp3")

    user1 = this.loadMazeAudioWithPrefix("game2-n1.mp3")
    user2 = this.loadMazeAudioWithPrefix("game2-n2.mp3")
    user3 = this.loadMazeAudioWithPrefix("game2-n3.mp3")
    user4 = this.loadMazeAudioWithPrefix("game2-n4.mp3")

    cigs = [
        this.loadMazeAudioWithPrefix("game3-cig-n1.mp3"),
        this.loadMazeAudioWithPrefix("game3-cig-n2.mp3"),
        this.loadMazeAudioWithPrefix("game3-cig-n3.mp3"),
        this.loadMazeAudioWithPrefix("game3-cig-n4.mp3"),
        this.loadMazeAudioWithPrefix("game3-cig-n5.mp3"),
        this.loadMazeAudioWithPrefix("game3-cig-n6.mp3"),
    ]
    ecigs = [
        this.loadMazeAudioWithPrefix("game3-ecig-n1.mp3"),
        this.loadMazeAudioWithPrefix("game3-ecig-n2.mp3"),
        this.loadMazeAudioWithPrefix("game3-ecig-n3.mp3"),
        this.loadMazeAudioWithPrefix("game3-ecig-n4.mp3"),
        this.loadMazeAudioWithPrefix("game3-ecig-n5.mp3"),
        this.loadMazeAudioWithPrefix("game3-ecig-n6.mp3"),
    ]
    sfxVolume = 0.4
    clock = this.loadMazeAudio("clock.mp3")
    bell = this.loadMazeAudio("bell.mp3")
    buzzer = this.loadMazeAudio("buzzer.mp3")
    timesUpBuzzer = this.loadMazeAudio("times-up-buzzer.mp3")
}
